import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAllBooks } from '@chinese-reader/shared';
import type { Book } from '@chinese-reader/shared';

// The Console's way into a Book. Issue 07 replaces this with the real Book page and grows the
// `/book/:id` stub rather than adding a second route; until then this is the entrance.
//
// One table, filtered — not two lists. But state is a first-class filter rather than something to
// type into the search box: in-review Books are the work and published ones are reference, and an
// admin should not have to guess that "in review" happens to match the free-text filter.
export default function Books() {
  const [books, setBooks] = useState<Array<Book & { page_count: number }> | null>(null);
  const [failure, setFailure] = useState('');

  useEffect(() => {
    getAllBooks()
      .then(setBooks)
      .catch((error) => setFailure(error instanceof Error ? error.message : String(error)));
  }, []);

  const [scope, setScope] = useState<'all' | 'in_review' | 'published'>('in_review');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<'updated_at' | 'title' | 'state'>('updated_at');
  const shown = useMemo(() => [...(books ?? [])].filter((book) => (scope === 'all' || book.state === scope) && `${book.title} ${book.state}`.toLowerCase().includes(query.toLowerCase())).sort((a, b) => sort === 'title' ? a.title.localeCompare(b.title) : sort === 'state' ? a.state.localeCompare(b.state) : b.updated_at.localeCompare(a.updated_at)), [books, scope, query, sort]);

  return (
    <div className="books-view">
      <div className="book-filters"><div className="scope-toggle" role="group" aria-label="Book state">{([['in_review', 'In review'], ['published', 'Published'], ['all', 'All']] as const).map(([value, label]) => <button key={value} className={scope === value ? 'scope active' : 'scope'} aria-pressed={scope === value} onClick={() => setScope(value)}>{label} <span className="count">{value === 'all' ? books?.length ?? 0 : books?.filter((b) => b.state === value).length ?? 0}</span></button>)}</div><input aria-label="Filter Books" placeholder="Filter Books" value={query} onChange={(e) => setQuery(e.target.value)} /><select aria-label="Sort Books" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}><option value="updated_at">Recently updated</option><option value="title">Title</option><option value="state">State</option></select></div>

      {failure && <p className="error" role="alert">{failure}</p>}
      {books === null && !failure && <p className="muted">Loading…</p>}
      {shown?.length === 0 && <p className="empty">No Books match.</p>}
      <table><thead><tr><th>Book</th><th>State</th><th>Spreads</th><th>Updated</th></tr></thead><tbody>{shown?.map((book) => <tr key={book.id}><td><Link to={`/book/${book.id}`}>{book.title}</Link></td><td>{book.state.replace('_', ' ')}</td><td>{book.page_count}</td><td className="muted">{new Date(book.updated_at).toLocaleDateString()}</td></tr>)}</tbody></table>
    </div>
  );
}
